import { BillableItemUnit } from "@api/graphql/generated/generated-types";
import { datePickerDateToISODate } from "@src/common/utils/timezoneConverters";

export const billableItemUnitOptions: { id: BillableItemUnit; label: string }[] = [
  { id: BillableItemUnit.PerWeek, label: "Per Week" },
  { id: BillableItemUnit.PerMonth, label: "Per Month" },
  { id: BillableItemUnit.PerStop, label: "Per Stop" },
  { id: BillableItemUnit.PerDay, label: "Per Day" },
  { id: BillableItemUnit.PerBusinessDay, label: "Per Business Day" },
  {
    id: BillableItemUnit.PerDayWithCompletedStops,
    label: "Per Day With Completed Stops",
  },
];

export function getBillableItemUnitLabel(unit: BillableItemUnit): string {
  switch (unit) {
    case BillableItemUnit.PerWeek:
      return "Per Week";
    case BillableItemUnit.PerMonth:
      return "Per Month";
    case BillableItemUnit.PerStop:
      return "Per Stop";
    case BillableItemUnit.PerDay:
      return "Per Day";
    case BillableItemUnit.PerBusinessDay:
      return "Per Business Day";
    case BillableItemUnit.PerDayWithCompletedStops:
      return "Per Day With Completed Stops";
    default:
      return unit;
  }
}

/** One line under the unit picker explaining how the selected unit is charged. */
export function getBillableItemUnitDescription(unit: BillableItemUnit): string {
  switch (unit) {
    case BillableItemUnit.PerWeek:
      return "A fixed weekly amount, prorated by day for partial periods.";
    case BillableItemUnit.PerMonth:
      return "A fixed monthly amount, prorated by day for partial periods.";
    case BillableItemUnit.PerStop:
      return "Charged for every pick-up, drop-off and return on the assigned orders.";
    case BillableItemUnit.PerDay:
      return "Charged for every calendar day in the invoice period.";
    case BillableItemUnit.PerBusinessDay:
      return "Charged for every Monday to Friday in the invoice period. Holidays are still charged.";
    case BillableItemUnit.PerDayWithCompletedStops:
      return "Charged only for days with at least one completed stop on the assigned orders.";
    default:
      return "";
  }
}

export interface SelectedEndCustomer {
  id: string;
  label: string;
}

/** The shape the form produces. Defined here (not in the form) so this util has no
 *  forward dependency on components; BillableItemForm imports and re-exports it. */
export interface BillableItemFormData {
  name: string;
  amount: string;
  unit: BillableItemUnit;
  serviceTypeId: string;
  endCustomers: SelectedEndCustomer[];
  startDate: Date | null;
  endDate: Date | null;
}

export interface BillableItemVariables {
  name: string;
  amount: string;
  unit: BillableItemUnit;
  serviceTypeId: string;
  endCustomerIds: string[];
  startDate: string | null;
  endDate: string | null;
}

export function buildBillableItemVariables(
  data: BillableItemFormData
): BillableItemVariables {
  return {
    name: data.name.trim(),
    amount: data.amount,
    unit: data.unit,
    serviceTypeId: data.serviceTypeId,
    endCustomerIds: (data.endCustomers ?? []).map((endCustomer) => endCustomer.id),
    startDate: data.startDate ? datePickerDateToISODate(data.startDate) : null,
    endDate: data.endDate ? datePickerDateToISODate(data.endDate) : null,
  };
}
